import { useRouter } from 'next/router'
import StateInitializer from '../../components/StateInitializer'
import Layout from '../../components/Layout'
import s from './login.module.css'
import PropTypes from 'prop-types'
import { createApolloClient } from '../../lib/createApolloClient'
import getGlobalSettings from '../../helpers/getGlobalSettings'
import getSites from '../../helpers/getSites'
import getSitesForStaticPaths from '../../helpers/getSitesForStaticPaths'
import Blocks from '../../components/Blocks'
import LOGIN_QUERY from '../../graphql/login.query'
// Import React hooks needed for this solution
import { useEffect, useCallback, useState } from 'react'
import * as Sentry from '@sentry/nextjs'

function Login({ globalSettings, sites, siteSlug, acfBlocks = {}, site = '' }) {
  const [error, setError] = useState(null)
  const router = useRouter()

  // 1. Define the submit handler using useCallback
  const handleSubmit = useCallback(
    async (event) => {
      // Note: When attached imperatively, the event object is the native browser event.
      // The logic inside should work fine with both native and React synthetic events.
      event.preventDefault()

      setError(null)

      // Ensure the event target is a form element before creating FormData
      const formElement = event.currentTarget || event.target
      if (formElement.tagName !== 'FORM') {
        console.error('Event target is not a form element.')
        return
      }

      const formData = new FormData(formElement)
      const username = formData.get('username')
      const password = formData.get('password')

      try {
        const response = await fetch('/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password }),
        })

        if (response.ok) {
          console.log('Reloading the page')
          await router.reload()
          console.log('Reloaded the page')
        } else {
          // You should extract and display the error message here
          const errorData = await response.json()
          console.error('Login failed:', errorData.message || 'Unknown error')
          setError(errorData.message || 'Unknown error')
          // e.g., Set a state variable to display an error message on the page
        }
      } catch (error) {
        console.error('Network or unexpected error during login:', error)
      }
    },
    [router]
  ) // Add router as a dependency for useCallback

  // 2. Attach the listener after the component renders and the Blocks component injects the HTML
  useEffect(() => {
    const formElement = document.getElementById('login-form')
    if (formElement) {
      // Add/remove error class
      if (error) {
        formElement.classList.add('error')
      } else {
        formElement.classList.remove('error')
      }

      // Insert error message into a specific container inside the form
      const errorContainer = document.getElementById('login-error')
      if (errorContainer) {
        errorContainer.textContent = error || ''
      }

      formElement.addEventListener('submit', handleSubmit)
      return () => {
        formElement.removeEventListener('submit', handleSubmit)
      }
    }
  }, [handleSubmit, error])

  return (
    <StateInitializer
      globalSettings={globalSettings}
      sites={sites}
      siteSlug={siteSlug}
    >
      <Layout globalSettings={globalSettings}>
        <div className={s.container}>
          {/* This is where the raw HTML form is rendered, likely via dangerouslySetInnerHTML */}
          {acfBlocks && <Blocks postType="Page">{acfBlocks}</Blocks>}
        </div>
      </Layout>
    </StateInitializer>
  )
}

Login.propTypes = {
  globalSettings: PropTypes.object.isRequired,
  sites: PropTypes.array.isRequired,
  siteSlug: PropTypes.string.isRequired,
}

export default Login

// The getStaticProps and getStaticPaths functions remain unchanged
// ... (omitted for brevity)

export async function getStaticProps({ params }) {
  const { site } = params

  const apolloClient = createApolloClient()

  const [globalSettings, sites, { data, error }] = await Promise.all([
    getGlobalSettings(site),
    getSites(),
    apolloClient.query({
      query: LOGIN_QUERY,
      context: { site },
      variables: {
        slug: 'about',
      },
    }),
  ])

  if (error) {
    Sentry.addBreadcrumb({
      category: 'getStaticProps',
      message: `Error loading Login props for site ${site}`,
      level: 'error',
      data: { params, error },
    })
    Sentry.captureException(new Error(`Error loading Login props - ${error}`))
    console.log(`Error loading Login props - ${error}`)
  }

  return {
    props: {
      ...params,
      ...data.pageBy,
      globalSettings,
      sites,
      siteSlug: site,
    },
    revalidate: 60 * 60 * 24, // 24 hours
  }
}

export async function getStaticPaths() {
  const sites = await getSitesForStaticPaths()

  const paths = sites
    .filter((site) => site === 'london')
    .map((site) => ({
      params: {
        site: site,
      },
    }))

  return {
    paths,
    fallback: false,
  }
}
